import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApiQuery } from '../../../../hooks/useApiQuery.js'
import { useMarkets } from '../../../../hooks/useMarkets.js'
import { useApiMutation } from '../../../../hooks/useApiMutation.js'
import { api } from '../../../../lib/api.js'
import { getApiErrorMessage } from '../../../../lib/apiErrors.js'
import { useToast } from '../../../../components/Toast/ToastProvider.jsx'
import { useAuthStore } from '../../../../stores/authStore.js'
import { isValidZipCode } from '../../../../components/OrderForm/validation/zipValidation.js'
import {
  toCanonicalPhone,
  toCanonicalEmail,
  toCanonicalZip,
} from '../../../../components/OrderForm/validation/fieldCanonical.js'

// ============================================
// Lane templates
// ============================================

export const laneTemplates = {
  delivery: {
    displayName: 'Delivery',
    legs: ['OutGateLoaded', 'AtConsignee'],
  },
  pickup: {
    displayName: 'Pickup',
    legs: ['AtShipper', 'InGateLoaded'],
  },
  crosstown: {
    displayName: 'Crosstown (Loaded)',
    legs: ['OutGateLoaded', 'InGateLoaded'],
  },
  empty_repo: {
    displayName: 'Empty Repo',
    legs: ['OutGateEmpty', 'InGateEmpty'],
  },
  delivery_two_stop: {
    displayName: 'Delivery (2 stops)',
    legs: ['OutGateLoaded', 'AtConsignee', 'AtConsignee'],
  },
  delivery_one_way: {
    displayName: 'Delivery (1 way)',
    legs: ['OutGateLoaded', 'AtConsignee'],
  },
  pickup_two_stop: {
    displayName: 'Pickup (2 stops)',
    legs: ['AtShipper', 'AtShipper', 'InGateLoaded'],
  },
  pickup_one_way: {
    displayName: 'Pickup (1 way)',
    legs: ['AtShipper', 'InGateLoaded'],
  },
  delivery_and_pickup: {
    displayName: 'Delivery and Pickup',
    legs: ['OutGateLoaded', 'AtConsignee', 'AtShipper', 'InGateLoaded'],
  },
}

const emptyLeg = (legType) => ({
  type: legType,
  title: legType.replace(/([A-Z])/g, ' $1').trim(),
  // 'place' = picked from PlaceSelect, 'manual' = user typed the address.
  // Lives in form state so the Zod schema can branch on it.
  addressMode: 'place',
  address: {
    name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
  },
  contact: { name: '', email: '', phone: '' },
  properties: {
    specialInstructions: '',
    deliveryInstructions: '',
    commodity: '',
  },
  place_id: '',
  formatted_address: '',
})

const generateTemplateLegs = (laneType) =>
  laneTemplates[laneType].legs.map(emptyLeg)

const emptyAdministration = {
  flags: [],
  label: '',
  lockCarrierPrice: null,
  carrierPrice: null,
  note: '',
  carrierUid: null,
  driverUserUid: null,
}

// ============================================
// Schema
// ============================================

// FormInput's mask branch parses currency/percent inputs to numbers, but null
// (the default) and '' (a stray empty) need to coerce to undefined so the
// inner number schema decides required/optional cleanly.
const optionalPrice = (label) =>
  z.preprocess(
    (val) =>
      val === null ||
      val === '' ||
      val === undefined ||
      (typeof val === 'number' && isNaN(val))
        ? undefined
        : val,
    z.number().min(0, `${label} cannot be negative`).optional(),
  )

const requiredPrice = (label) =>
  z.preprocess(
    (val) =>
      val === null ||
      val === '' ||
      val === undefined ||
      (typeof val === 'number' && isNaN(val))
        ? undefined
        : val,
    z
      .number({
        required_error: `${label} is required`,
        invalid_type_error: `${label} must be a number`,
      })
      .min(0, `${label} cannot be negative`),
  )

const legSchema = z
  .object({
    type: z.string(),
    title: z.string().optional(),
    uid: z.string().optional(),
    addressMode: z.enum(['place', 'manual']).default('place'),
    address: z
      .object({
        name: z.string().optional(),
        street1: z.string().optional(),
        street2: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zip: z.string().optional(),
      })
      .optional(),
    contact: z
      .object({
        name: z.string().optional(),
        email: z.string().optional(),
        phone: z.string().optional(),
      })
      .optional(),
    properties: z
      .object({
        deliveryInstructions: z.string().optional(),
        specialInstructions: z.string().optional(),
        commodity: z.string().optional(),
      })
      .optional(),
    place_id: z.union([z.string(), z.number()]).nullable().optional(),
    formatted_address: z.string().optional(),
    placeData: z.any().nullable().optional(),
  })
  .superRefine((leg, ctx) => {
    // Address mode validation (migrated from OrderForm's validateAddressFields)
    if (leg.addressMode === 'manual') {
      if (!leg.address?.name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'name'],
          message: 'Name is required',
        })
      }
      if (!leg.address?.street1?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'street1'],
          message: 'Address is required',
        })
      }
      if (!leg.address?.city?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'city'],
          message: 'City is required',
        })
      }
      if (!leg.address?.state?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'state'],
          message: 'State is required',
        })
      }
      if (!leg.address?.zip?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'zip'],
          message: 'Zip is required',
        })
      } else if (!isValidZipCode(leg.address.zip)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['address', 'zip'],
          message: 'Invalid zip code format',
        })
      }
    } else {
      const hasPlace =
        leg.placeData?.uid || leg.placeData?.place_id || leg.place_id
      if (!hasPlace) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['placeData'],
          message: 'Please select a place or enter address manually',
        })
      }
    }
    // Phone (DN-only — lane editor is DN-only per multitenancy.js: lanes flag).
    const phone = leg.contact?.phone || ''
    if (phone.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contact', 'phone'],
        message: 'Phone is required',
      })
    } else if (!/^\d{10}$/.test(phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contact', 'phone'],
        message: 'Phone must be 10 digits',
      })
    }
    // Email format (only when present)
    const email = leg.contact?.email
    if (
      email &&
      email.trim() !== '' &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contact', 'email'],
        message: 'Invalid email address',
      })
    }
  })

const laneSchema = z.object({
  uid: z.string().optional(),
  name: z.string().trim().min(1, 'Lane name is required'),
  marketUid: z.string().min(1, 'Market is required'),
  type: z.string().min(1, 'Type is required'),
  state: z.string().default('active'),
  imcCompanyUid: z.string().min(1, 'Company is required'),
  imcCompany: z.any().optional(),
  imcPrice: requiredPrice('Price'),
  fuelSurchargePercent: optionalPrice('Fuel surcharge percent'),
  legs: z.array(legSchema).min(2, 'Lane must have at least two legs'),
  administration: z
    .object({
      flags: z.array(z.string()).optional(),
      label: z.string().optional(),
      carrierPrice: optionalPrice('Carrier price'),
      lockCarrierPrice: optionalPrice('Carrier price'),
      carrierUid: z.string().nullable().optional(),
      driverUserUid: z.string().nullable().optional(),
      note: z.string().optional(),
    })
    .optional(),
})

// ============================================
// Helpers
// ============================================

const buildDefaultValues = (propLane, currentUserCompanyUid) => {
  if (!propLane) {
    return {
      uid: undefined,
      name: '',
      marketUid: '',
      type: 'delivery',
      state: 'active',
      imcCompanyUid: currentUserCompanyUid || null,
      imcCompany: {},
      imcPrice: null,
      fuelSurchargePercent: null,
      legs: generateTemplateLegs('delivery'),
      administration: { ...emptyAdministration },
    }
  }
  return {
    uid: propLane.uid,
    name: propLane.name || '',
    marketUid: propLane.marketUid || '',
    type: propLane.type || 'delivery',
    state: propLane.state || 'active',
    imcCompanyUid: propLane.imcCompanyUid || currentUserCompanyUid || null,
    imcCompany: propLane.imcCompany || {},
    imcPrice: propLane.imcPrice ?? null,
    fuelSurchargePercent: propLane.fuelSurchargePercent ?? null,
    legs:
      propLane.legs && propLane.legs.length > 0
        ? propLane.legs.map((leg) => {
            const baseAddress = {
              ...emptyLeg(leg.type).address,
              ...(leg.address || {}),
            }
            const baseContact = {
              ...emptyLeg(leg.type).contact,
              ...(leg.contact || {}),
            }
            return {
              ...emptyLeg(leg.type),
              ...leg,
              // Same heuristic the legacy isCustomAddress useState used: a
              // saved leg with no place_id but a manually-entered address
              // name renders in manual mode. The place-resolution useEffect
              // below may flip this to 'manual' if a strict match fails.
              addressMode:
                !leg.place_id && !!leg.address?.name ? 'manual' : 'place',
              address: baseAddress,
              contact: {
                ...baseContact,
                phone: toCanonicalPhone(baseContact.phone),
              },
              properties: {
                ...emptyLeg(leg.type).properties,
                ...(leg.properties || {}),
              },
              // PlaceSelect (lookupBy='place_id') reads this to render the
              // saved place as the dropdown's selected option on edit. The
              // backend doesn't persist formatted_address on the leg, so
              // PlaceSelect derives the dropdown label from `address` instead.
              placeData: leg.place_id
                ? { place_id: leg.place_id, address: baseAddress }
                : null,
            }
          })
        : generateTemplateLegs(propLane.type || 'delivery'),
    administration: {
      ...emptyAdministration,
      ...(propLane.administration || {}),
    },
  }
}

// Convert backend validation error path (e.g. "legs[0].address.name") to RHF path
// (e.g. "legs.0.address.name") so setError targets the right field.
const apiPathToFormPath = (path) => path.replace(/\[(\d+)\]/g, '.$1')

// Strip empty strings / null / undefined from a one-level-deep object.
// Server's obey schema rejects empty strings on optional fields (e.g.
// address.street2, contact.email) and rejects keys not present in the
// per-leg-type schema (e.g. commodity on InGateLoaded). Legacy stripped
// these before submit; we mirror that.
const stripEmpties = (obj) => {
  if (!obj || typeof obj !== 'object') return obj
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== '' && v !== null && v !== undefined,
    ),
  )
}

// Build the leg shape the backend accepts: drop transient form-only fields
// (title, formatted_address, placeData, addressMode) and strip empty values
// from address/contact/properties. place_id is also dropped when empty/null —
// the column is type=number, so '' would fail server validation.
const toApiLeg = (leg) => {
  const {
    title: _title,
    formatted_address: _formattedAddress,
    placeData: _placeData,
    addressMode: _addressMode,
    place_id,
    ...rest
  } = leg
  const result = {
    ...rest,
    address: stripEmpties(rest.address),
    contact: stripEmpties(rest.contact),
    properties: stripEmpties(rest.properties),
  }
  if (place_id != null && place_id !== '') {
    result.place_id = place_id
  }
  return result
}

// ============================================
// Main hook
// ============================================

export function useLaneEditorFormLogic({ propLane, onClose }) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const queryClient = useQueryClient()
  const addToast = useToast()

  const defaultValues = useMemo(
    () => buildDefaultValues(propLane, user?.companyUid),
    [propLane, user?.companyUid],
  )

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(laneSchema),
    defaultValues,
  })

  const { control, setValue, getValues, setError, trigger, formState } = form

  const legsArray = useFieldArray({ control, name: 'legs' })

  // ============================================
  // Watched values used to drive view logic
  // ============================================

  const laneType = useWatch({ control, name: 'type' })
  const marketUid = useWatch({ control, name: 'marketUid' })
  const adminLabel = useWatch({ control, name: 'administration.label' })
  const carrierUid = useWatch({ control, name: 'administration.carrierUid' })

  // ============================================
  // Per-leg "manual address" toggle
  // ============================================

  // addressMode lives in form state so the Zod schema can branch on it.
  // The helper below writes to RHF; no separate component state to keep in
  // sync. Initial value is derived per-leg in buildDefaultValues.
  const setLegCustomAddress = useCallback(
    (idx, value) => {
      setValue(`legs.${idx}.addressMode`, value ? 'manual' : 'place', {
        shouldDirty: true,
        shouldValidate: false,
      })
    },
    [setValue],
  )

  // ============================================
  // Edit-mode hydration: resolve each leg's saved place into a full backend
  // place object so PlaceSelect's value is a real place (not a synthesized
  // stub). For each leg with a saved place_id, search by the leg's place name
  // and strict-match the results against the leg's address fields. Exactly
  // one strict match becomes the leg's placeData; 0 or 2+ matches means we
  // treat the leg as a manual-address entry.
  // ============================================

  const placeResolutionRef = useRef(null)
  useEffect(() => {
    if (!propLane?.uid) return
    if (placeResolutionRef.current === propLane.uid) return
    placeResolutionRef.current = propLane.uid

    const norm = (v) => (v ?? '').toString().trim().toLowerCase()
    const STRICT_MATCH_FIELDS = [
      'name',
      'street1',
      'street2',
      'city',
      'state',
      'zip',
    ]
    const isStrictMatch = (legAddress, place) =>
      STRICT_MATCH_FIELDS.every(
        (f) => norm(legAddress?.[f]) === norm(place.address?.[f]),
      )

    let cancelled = false
    const legsSnapshot = getValues('legs') || []

    Promise.all(
      legsSnapshot.map(async (leg, idx) => {
        if (!leg.place_id) return { idx, place: null, skip: true }
        const name = leg.address?.name
        if (!name) return { idx, place: null, skip: false }
        try {
          // Don't filter by market: the lane's market may differ from the
          // saved place's market (places are picked freely at lane creation).
          // Strict-match on address fields is enough to pick the right place.
          const data = await api.searchPlaces({
            query: { searchQuery: name },
            is_active: true,
          })
          const results = data?.results || []
          const matches = results.filter((p) => isStrictMatch(leg.address, p))
          if (matches.length === 1) return { idx, place: matches[0] }
          return { idx, place: null }
        } catch {
          return { idx, place: null }
        }
      }),
    ).then((outcomes) => {
      if (cancelled) return
      // Hydration writes — keep the form clean (shouldDirty: false). The
      // setLegCustomAddress helper marks dirty (intended for user actions),
      // so write addressMode directly here.
      outcomes.forEach(({ idx, place, skip }) => {
        if (skip) return
        const opts = { shouldDirty: false, shouldValidate: false }
        if (place) {
          setValue(`legs.${idx}.placeData`, place, opts)
          setValue(`legs.${idx}.addressMode`, 'place', opts)
        } else {
          setValue(`legs.${idx}.placeData`, null, opts)
          setValue(`legs.${idx}.addressMode`, 'manual', opts)
        }
      })
    })

    return () => {
      cancelled = true
    }
  }, [propLane, getValues, setValue])

  // ============================================
  // Type change: regenerate template legs
  // ============================================

  const prevTypeRef = useRef(getValues('type'))

  const handleTypeChange = useCallback(
    (newType) => {
      if (newType === prevTypeRef.current) return
      prevTypeRef.current = newType
      setValue('name', '', { shouldDirty: true })
      hasPrefilledNameRef.current = false
      legsArray.replace(generateTemplateLegs(newType))
    },
    [setValue, legsArray],
  )

  // ============================================
  // Auto-prefill lane name from leg address names
  // ============================================

  // One-shot prefill: the first time legs produce a non-empty auto-built
  // name, write it to the form and latch the ref. After that the field is
  // hands-off — the user can edit, delete, or leave it alone, and further
  // leg picks won't override. The ref resets on lane-type change (which
  // wipes the legs to template defaults). Edit-mode lanes start latched
  // so the saved name isn't overwritten on mount.
  const hasPrefilledNameRef = useRef(Boolean(propLane?.name))
  const watchedLegs = useWatch({ control, name: 'legs' })
  const legAddressKey = watchedLegs
    ?.map((l) => l?.address?.name || '')
    .join('|')
  useEffect(() => {
    if (hasPrefilledNameRef.current) return
    if (!watchedLegs?.length) return
    const next = watchedLegs.reduce((acc, leg) => {
      const placeName = leg?.address?.name
      if (!placeName) return acc
      return acc ? `${acc} to ${placeName}` : placeName
    }, '')
    if (!next) return
    setValue('name', next, { shouldDirty: false, shouldValidate: false })
    hasPrefilledNameRef.current = true
  }, [legAddressKey, watchedLegs, setValue])

  // ============================================
  // Place selection per leg
  // ============================================

  const handlePlaceSelect = useCallback(
    (idx, place) => {
      if (!place) return
      if (place.isCleared) {
        // User cleared the dropdown — wipe place + address + contact so we
        // don't submit a leg that still references the old place_id.
        const currentLeg = getValues(`legs.${idx}`)
        setLegCustomAddress(idx, false)
        setValue(`legs.${idx}.place_id`, '', { shouldDirty: true })
        setValue(`legs.${idx}.formatted_address`, '', { shouldDirty: true })
        setValue(
          `legs.${idx}.address`,
          { ...emptyLeg(currentLeg.type).address },
          { shouldDirty: true },
        )
        setValue(
          `legs.${idx}.contact`,
          { ...emptyLeg(currentLeg.type).contact },
          { shouldDirty: true },
        )
        return
      }
      if (place.isManualEntry) {
        setLegCustomAddress(idx, true)
        const currentLeg = getValues(`legs.${idx}`)
        // Clear place_id so we don't submit a stale FK alongside manual fields.
        setValue(`legs.${idx}.place_id`, '', { shouldDirty: true })
        setValue(`legs.${idx}.formatted_address`, '', { shouldDirty: true })
        setValue(
          `legs.${idx}.address`,
          { ...emptyLeg(currentLeg.type).address },
          { shouldDirty: true },
        )
        setValue(
          `legs.${idx}.contact`,
          { ...currentLeg.contact, phone: '' },
          { shouldDirty: true },
        )
        return
      }
      setLegCustomAddress(idx, false)
      const currentLeg = getValues(`legs.${idx}`)
      setValue(
        `legs.${idx}.address`,
        {
          ...emptyLeg(currentLeg.type).address,
          ...(place.address || {}),
          zip: toCanonicalZip(place.address?.zip),
        },
        { shouldDirty: true },
      )
      setValue(
        `legs.${idx}.contact`,
        {
          ...currentLeg.contact,
          // No trailing `|| ''`. It was unreachable twice over: buildDefaultValues
          // seeds every leg's contact.phone through toCanonicalPhone, so it is
          // '' and never undefined, and toCanonicalPhone maps null/undefined to
          // '' anyway (fieldCanonical.js). Nothing observable distinguished the
          // operand's presence, so it went rather than getting a vacuous test.
          // The canonicalizer itself stays — it normalizes a real place's masked
          // phone, which the tests above cover.
          phone: toCanonicalPhone(
            place?.contact?.phone || currentLeg.contact?.phone,
          ),
          email: toCanonicalEmail(
            place?.contact?.email || currentLeg.contact?.email || '',
          ),
          name: place?.contact?.name || currentLeg.contact?.name || '',
        },
        { shouldDirty: true },
      )
      setValue(`legs.${idx}.place_id`, place.place_id || '', {
        shouldDirty: true,
      })
      setValue(`legs.${idx}.formatted_address`, place.formatted_address || '', {
        shouldDirty: true,
      })
    },
    [setValue, getValues, setLegCustomAddress],
  )

  // ============================================
  // Reference data queries
  // ============================================

  const { availableMarkets } = useMarkets({ showToast: true })
  const marketOptions = useMemo(
    () =>
      availableMarkets.map((m) => ({ key: m.uid, value: m.uid, text: m.name })),
    [availableMarkets],
  )

  const { data: laneDefs } = useApiQuery({
    key: ['getLaneModelDefinitions'],
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
  })
  const labelOptions = useMemo(() => {
    const allowed = laneDefs?.administration?.keys?.label?.allow || []
    return [
      { key: '', value: '', text: 'NONE' },
      ...allowed.map((i) => ({ key: i, value: i, text: i })),
    ]
  }, [laneDefs])

  const { data: statesData } = useApiQuery({
    key: ['getStatesTerritories'],
    staleTime: 5 * 60 * 1000,
  })
  const stateOptions = useMemo(() => {
    if (!statesData) return []
    return [
      { key: 0, value: '', text: '' },
      ...Object.keys(statesData).map((abbr) => ({
        key: abbr,
        value: abbr,
        text: statesData[abbr],
      })),
    ]
  }, [statesData])

  // ============================================
  // IMC company search (admin-only)
  // ============================================

  const [imcSearchValue, setImcSearchValue] = useState('')
  // Mirror the prefilled company as a selectable option so the dropdown can
  // render it as the current selection. Without this, the form value (a UID)
  // exists but no matching option is loaded yet (search query is gated on a
  // non-empty input), so FormSelect would show only the placeholder.
  const [prefilledImcOption, setPrefilledImcOption] = useState(null)
  const imcInitializedRef = useRef(false)
  useEffect(() => {
    if (imcInitializedRef.current) return
    if (!defaultValues.imcCompanyUid) return
    if (!isAdmin) return
    imcInitializedRef.current = true
    api
      .getCompanyInfo(defaultValues.imcCompanyUid)
      .then((res) => {
        setImcSearchValue(res.name)
        setPrefilledImcOption({ key: res.uid, value: res.uid, text: res.name })
      })
      // Without this the rejection is unhandled, and the failure is silent
      // where it matters: the form value still holds the uid, but with no
      // option to match it FormSelect renders its placeholder, so a `required`
      // field with a company looks unset. An admin who "fills it in" then
      // reassigns the lane's IMC. The ref latches above, before the request,
      // so nothing retries — the toast is the only signal there is.
      .catch((err) => {
        addToast(
          getApiErrorMessage(err) || 'Failed to load the assigned company',
          'error',
        )
      })
  }, [defaultValues.imcCompanyUid, addToast])

  const { data: imcSearchData, isLoading: imcLoading } = useApiQuery({
    key: [
      'searchCompanies',
      { query: imcSearchValue, is_active: true, company_type: 'imc' },
    ],
    payload: { query: imcSearchValue, is_active: true, company_type: 'imc' },
    enabled: Boolean(imcSearchValue),
    showToast: false,
  })

  const imcOptions = useMemo(() => {
    const companies = (imcSearchData?.results || []).map((c) => ({
      key: c.uid,
      value: c.uid,
      text: c.name,
    }))
    if (
      prefilledImcOption &&
      !companies.some((c) => c.value === prefilledImcOption.value)
    ) {
      companies.unshift(prefilledImcOption)
    }
    return [{ key: 0, value: '', text: '' }, ...companies]
  }, [imcSearchData, prefilledImcOption])

  // ============================================
  // Carrier company search (admin-only, gated by Awarded label)
  // ============================================

  const [carrierSearchValue, setCarrierSearchValue] = useState('')
  const carrierInitializedRef = useRef(false)
  useEffect(() => {
    const initialCarrierUid = defaultValues.administration?.carrierUid
    if (carrierInitializedRef.current) return
    if (!initialCarrierUid) return
    if (!isAdmin) return
    carrierInitializedRef.current = true
    api
      .getCompanyInfo(initialCarrierUid)
      .then((res) => {
        setCarrierSearchValue(res.name)
      })
      // Same reasoning as the IMC prefill above. The carrier select degrades
      // more quietly still: it has no prefilled-option fallback at all, so its
      // options come only from searchCompanies matching this name.
      .catch((err) => {
        addToast(
          getApiErrorMessage(err) || 'Failed to load the assigned carrier',
          'error',
        )
      })
  }, [defaultValues.administration?.carrierUid, addToast])

  const { data: carrierSearchData, isLoading: carrierLoading } = useApiQuery({
    key: [
      'searchCompanies',
      { query: carrierSearchValue, is_active: true, company_type: 'carrier' },
    ],
    payload: {
      query: carrierSearchValue,
      is_active: true,
      company_type: 'carrier',
    },
    enabled: Boolean(carrierSearchValue),
    showToast: false,
  })

  const carrierOptions = useMemo(() => {
    const companies = (carrierSearchData?.results || []).map((c) => ({
      key: c.uid,
      value: c.uid,
      text: c.name,
    }))
    return [{ key: 0, value: '', text: '' }, ...companies]
  }, [carrierSearchData])

  // ============================================
  // Drivers (admin-only, gated by carrier selection)
  // ============================================

  const { data: driversData = [], isLoading: driversLoading } = useApiQuery({
    key: ['getUsers', { ability: 'canDrive', companyUid: carrierUid }],
    payload: { ability: 'canDrive', companyUid: carrierUid },
    enabled: Boolean(carrierUid),
    showToast: false,
  })

  const driverOptions = useMemo(() => {
    const drivers = driversData.map((u) => ({
      key: u.uid,
      value: u.uid,
      text: `${u.firstName} ${u.lastName} - ${u.company?.name || ''}`,
    }))
    return [{ key: '', value: '', text: 'NONE' }, ...drivers]
  }, [driversData])

  // ============================================
  // Submit
  // ============================================

  const saveLaneMutation = useApiMutation({
    method: 'saveLane',
    showSuccessToast: true,
    successMessage: 'Lane saved',
  })

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.()

      // Single Zod pass covers every field including per-leg address-mode,
      // phone, and email rules (see legSchema.superRefine).
      const valid = await trigger()
      if (!valid) return

      const values = getValues()
      const payload = {
        ...values,
        legs: values.legs.map(toApiLeg),
      }
      saveLaneMutation.mutate(payload, {
        onSuccess: () => {
          // Invalidate synchronously here (not via useApiMutation's
          // invalidateKeys) because onClose unmounts the modal — and the
          // mutation hook with it — before the success effect can fire.
          queryClient.invalidateQueries({ queryKey: ['getLanes'] })
          onClose?.()
        },
        onError: (err) => {
          const status = err?.response?.status
          const data = err?.response?.data
          if (status !== 400 || !Array.isArray(data)) return
          data.forEach((entry) => {
            if (Array.isArray(entry.value) && entry.message) {
              Object.keys(entry.message).forEach((legIdx) => {
                entry.message[legIdx].forEach((legErr) => {
                  setError(
                    apiPathToFormPath(`${entry.key}[${legIdx}].${legErr.key}`),
                    { type: 'server', message: legErr.message },
                  )
                })
              })
              return
            }
            setError(apiPathToFormPath(entry.key), {
              type: 'server',
              message: entry.message,
            })
          })
        },
      })
    },
    [saveLaneMutation, onClose, setError, trigger, getValues, queryClient],
  )

  // ============================================
  // Public interface
  // ============================================

  return {
    control,
    formState,
    handleSubmit,
    // Live legs values — the view indexes them per leg for addressMode
    watchedLegs: watchedLegs || [],
    legsFields: legsArray.fields,
    laneType,
    marketUid,
    adminLabel,
    carrierUid,
    isAdmin,
    setLegCustomAddress,
    handleTypeChange,
    handlePlaceSelect,
    onCancel: onClose,
    isSubmitting: saveLaneMutation.isPending,
    options: {
      type: Object.keys(laneTemplates).map((key) => ({
        key,
        value: key,
        text: laneTemplates[key].displayName,
      })),
      market: marketOptions,
      label: labelOptions,
      state: stateOptions,
      imc: {
        options: imcOptions,
        loading: imcLoading,
        searchValue: imcSearchValue,
        onSearchChange: setImcSearchValue,
      },
      carrier: {
        options: carrierOptions,
        loading: carrierLoading,
        searchValue: carrierSearchValue,
        onSearchChange: setCarrierSearchValue,
      },
      driver: {
        options: driverOptions,
        loading: driversLoading,
      },
    },
  }
}
